import * as Sentry from '@sentry/react';

interface ReportContext {
  /** Short, stable slug to filter the tracker by, e.g. 'property_gallery_upload'. */
  feature?: string;
  /** Any extra, NON-PII data to attach (ids, counts, status codes). */
  [key: string]: unknown;
}

/**
 * Report a handled (caught) error to GlitchTip/Sentry.
 *
 * Unhandled errors are auto-captured by the SDK. This is for the ones we
 * *swallow* — catch blocks that previously only did console.error + alert().
 * Those failures were invisible to the tracker, which is exactly why the
 * gallery upload bug went unseen while "multiple users" hit it.
 *
 * `feature` becomes a searchable tag; everything else in `context` is attached
 * as extra data. Never pass PII (emails, phones, names, message bodies) —
 * sentry.ts scrubs request bodies/headers, but not arbitrary extras passed here.
 */
/**
 * Coerce an arbitrary thrown/rejected value into an Error with usable context.
 *
 * Plain `new Error('Unknown error')` erased the cause of every non-Error
 * rejection — which is exactly what the photo pipeline throws: HEIC decoders reject
 * with plain `{ code, message }` objects, and browser-image-compression rejects
 * with raw DOM Events (img/FileReader `onerror`). Unwrap both so the tracker
 * shows the real failure instead of a useless "Unknown error".
 */
export function coerceError(error: unknown): Error {
  if (error instanceof Error) return error;
  if (typeof error === 'string') return new Error(error);
  if (typeof Event !== 'undefined' && error instanceof Event) {
    const target = (error.target as { constructor?: { name?: string } } | null)?.constructor?.name;
    return new Error(`DOM ${error.type} event on ${target ?? 'unknown'}`);
  }
  if (error && typeof error === 'object') {
    const o = error as { message?: unknown; code?: unknown };
    if (typeof o.message === 'string') {
      return new Error(o.code !== undefined ? `${o.message} (code ${String(o.code)})` : o.message);
    }
    try {
      return new Error(JSON.stringify(error).slice(0, 300));
    } catch {
      /* cyclic / non-serializable — fall through */
    }
  }
  return new Error('Unknown error');
}

/**
 * Mark a failure the USER caused and can fix — an oversized file, a rejected
 * file type. It is still thrown and still shown to them; it is just not a
 * defect, so it must not reach the tracker. Without this, every oversized
 * upload opened a GlitchTip issue that read like a crash (#916, #1124, #1146 —
 * "La imagen supera el límite de 15 MB (40 MB)"), burying real failures.
 */
export function expectedError(message: string): Error {
  return Object.assign(new Error(message), { expected: true });
}

export function reportError(error: unknown, context?: ReportContext): void {
  const { feature, ...extra } = context ?? {};
  const err = coerceError(error);

  // Handled validation the user can act on — surfaced to them, never tracked.
  if ((error as { expected?: boolean } | null)?.expected) {
    console.warn(feature ? `[${feature}]` : '[expected]', err.message);
    return;
  }

  Sentry.captureException(err, {
    tags: feature ? { feature } : undefined,
    extra: Object.keys(extra).length ? extra : undefined,
  });

  // Keep console output for local dev + as a breadcrumb in the next event.
  console.error(feature ? `[${feature}]` : '[error]', error);
}
